import { useQuery } from "@apollo/client";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Spinner } from "src/components/Spinner";
import { GetGiftHistory } from "src/graphql/mutations";
import useAppStore from "src/store/utils";
import { getGiftHistory, getGiftHistoryVariables } from "src/types/api";
import EmptyState from "./EmptyState";
import PaymentHistoryItem from "./PaymentHistoryItem";

const PaymentHistory = () => {
  const navigate = useNavigate();
  const { event } = useAppStore((state) => state);

  const { data: giftHistory, loading: giftHistoryLoading } = useQuery<
    getGiftHistory,
    getGiftHistoryVariables
  >(GetGiftHistory, {
    variables: {
      event_id: event.uuid,
    },
    fetchPolicy: "cache-and-network", // Use cache for faster initial render
    onError(error) {
      console.error("GiftHistory Error:", error);
      if (error.message === "unauthenticated") {
        navigate("/clear");
      } else {
        toast.error(
          <p className="toast">
            {error?.message || "Failed to load gift history"}
          </p>
        );
      }
    },
  });

  const gifts =
    giftHistory?.getGiftHistory?.gifts?.filter(
      (item): item is NonNullable<typeof item> => item !== null
    ) || [];

  const hasGifts = gifts.length > 0;

  // Skeleton UI for loading state
  const renderSkeleton = () => (
    <div className="">
      {Array(3)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className="m-2 h-16 animate-pulse rounded-lg bg-gray-200"
          />
        ))}
    </div>
  );

  return (
    <>
      {giftHistoryLoading && !hasGifts && renderSkeleton()}

      {hasGifts && (
        <div className="relative w-full md:w-[500px]">
          {giftHistoryLoading && (
            <div className="flex w-full items-center justify-center py-2">
              <Spinner />
            </div>
          )}
          <div
            className="max-h-[400px] w-full cursor-pointer overflow-auto rounded-2xl border pt-2"
            role="list"
          >
            {gifts.map((item) => (
              <PaymentHistoryItem key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {!giftHistoryLoading && !hasGifts && <EmptyState />}
    </>
  );
};

export default PaymentHistory;
